/**
 * Medical, step 1 — bandages (65.7; the owner, Oct 10: "medical concept, healing, limited medical
 * supplies, self healing animation and wait time (bandage apply, Hell Let Loose style)"), and step 2 —
 * the medic (65.8): a bandage on a teammate in reach.
 *
 * A person carries a few bandages (BANDAGE.carried; `bandages` absent = all of them, as a magazine
 * absent is full). Applying one is a timed state on the action clock — `healLeft` counts down from
 * `healTime` — that holds the person still with the weapon down (the Match refuses a shot meanwhile);
 * when it runs out the bandage is spent and `BANDAGE.heals` points come back, never past
 * `BANDAGE.maxHealth`. A step or a jump breaks it off (65.11; it was a shot — lane B's every-switch
 * check found 93 % of bandages abandoned to the bandager's own shot) with the bandage unspent and its
 * progress kept (`healSaved`): the next one resumes from there.
 *
 * The medic: with a hurt teammate within `BANDAGE.reach` people, the bandage goes on them instead —
 * the patient carries the timer (`healBy` = the medic) and is held like any patient; the medic carries
 * `treating` (the patient's id), is held too, and spends **their own** bandage when it is on. Nobody
 * is an unlimited source (the owner, Oct 10): the same two a life, whoever they go on. Pure; the
 * Match steps it (`stepTreatments` before the units act).
 */
import type { GameState, PlanetState, PlayerState, UnitIntent } from '../types';
import { BANDAGE } from '../constants';
import { PERSON_BODY_HEIGHT, personSetOf } from './scaleProfile';
import { getPlayerWorldPosition } from './gameState';

type Patient = Pick<PlayerState, 'health' | 'alive' | 'airborne' | 'mount' | 'bandages' | 'healLeft' | 'healTime' | 'healSaved' | 'reloadLeft' | 'switchLeft'>;

/** Bandages carried (absent = the full issue). */
export function bandagesLeft(p: Pick<PlayerState, 'bandages'>): number {
  return p.bandages ?? BANDAGE.carried;
}

/** Hands free: no reload, draw, bandage or treatment running. */
function handsFree(p: Pick<PlayerState, 'healLeft' | 'reloadLeft' | 'switchLeft' | 'treating'>): boolean {
  return p.healLeft === undefined && p.reloadLeft === undefined && p.switchLeft === undefined && p.treating === undefined;
}

/** Can a bandage start now: alive and hurt, on the ground and out of any seat, hands free (no reload, draw or bandage running), one carried. */
export function canBandage(p: Patient & Pick<PlayerState, 'treating'>): boolean {
  return p.alive && !p.airborne && !p.mount && p.health > 0 && p.health < BANDAGE.maxHealth && handsFree(p) && bandagesLeft(p) > 0;
}

/**
 * Why a bandage can't start on `p` now (for the HUD: the owner pressed H in the Lab and nothing said why), or null when
 * it can. The same conditions as `canBandage`, in the order a player would want to hear them.
 */
export function bandageRefusal(p: Patient & Pick<PlayerState, 'treating'>): string | null {
  if (canBandage(p)) return null;
  if (!p.alive || p.health <= 0) return 'down';
  if (p.mount) return 'get out of the seat first';
  if (p.airborne) return 'on the ground first';
  if (p.healLeft !== undefined) return 'already bandaging';
  if (p.treating !== undefined) return 'busy bandaging a teammate';
  if (p.health >= BANDAGE.maxHealth) return 'not hurt';
  if (bandagesLeft(p) <= 0) return 'no bandages — restock at a crate (E)';
  if (p.reloadLeft !== undefined) return 'hands busy — reloading';
  if (p.switchLeft !== undefined) return 'hands busy — drawing';
  return 'can\'t bandage now';
}

/** How far along the bandage is (0 just started … 1 done), or null when none is being applied. */
export function healProgress(p: Pick<PlayerState, 'healLeft' | 'healTime'>): number | null {
  if (p.healLeft === undefined) return null;
  const total = p.healTime ?? BANDAGE.seconds;
  return total > 0 ? Math.max(0, Math.min(1, 1 - p.healLeft / total)) : 1;
}

/** Start applying a bandage (the caller checks `canBandage`). */
export function beginBandage<T extends Patient>(p: T): T {
  // 65.11: an interrupted bandage resumes where it stopped (its progress kept, the bandage still unspent)
  return { ...p, healLeft: p.healSaved ?? BANDAGE.seconds, healTime: BANDAGE.seconds, healSaved: undefined };
}

/** The bandage is spent and the person healed, the timers cleared. */
export function applyBandage<T extends Patient>(p: T): T {
  return { ...p, health: Math.min(BANDAGE.maxHealth, p.health + BANDAGE.heals), bandages: bandagesLeft(p) - 1, healLeft: undefined, healTime: undefined, healSaved: undefined };
}

/** `dt` seconds of the bandage being applied; when it runs out it is applied. No-op when none is running. */
export function stepBandage<T extends Patient>(p: T, dt: number): T {
  if (p.healLeft === undefined) return p;
  return p.healLeft - dt > 0 ? { ...p, healLeft: p.healLeft - dt } : applyBandage(p);
}

/** Interrupted: the timers cleared, the bandage kept (a teammate's bandage too: the medic's `treating` clears on their next step). */
export function cancelBandage<T extends Patient & Pick<PlayerState, 'healBy'>>(p: T): T {
  if (p.healLeft === undefined) return p;
  // 65.11 (lane B's every-switch check: 93 % of bandages were abandoned to the bandager's own shot): one's own bandage
  // keeps its progress through an interruption — the next one resumes it; a teammate's (the medic's) starts over
  return { ...p, healLeft: undefined, healTime: undefined, healBy: undefined, ...(p.healBy === undefined ? { healSaved: p.healLeft } : {}) };
}

// --- the medic (65.8) -------------------------------------------------------

type Person = Pick<PlayerState, 'id' | 'teamIndex' | 'planetIndex' | 'surfaceDir' | 'alive' | 'airborne' | 'airbornePos' | 'mount' | 'health' | 'healLeft' | 'healBy' | 'treating' | 'reloadLeft' | 'switchLeft' | 'bandages'>;

/** The reach of a bandage on a teammate, in world units at this scale. */
export function medicReach(state: Pick<GameState, 'config'>): number {
  return BANDAGE.reach * PERSON_BODY_HEIGHT * personSetOf(state).k;
}

/** Ground distance between two people on the same planet (world units; Infinity on different planets). */
export function apart(a: Person, b: Person, planets: readonly PlanetState[]): number {
  if (a.planetIndex !== b.planetIndex) return Infinity;
  const pa = getPlayerWorldPosition(a as PlayerState, planets as PlanetState[]), pb = getPlayerWorldPosition(b as PlayerState, planets as PlanetState[]);
  const dx = pa.x - pb.x, dy = pa.y - pb.y, dz = pa.z - pb.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Can `medic` put a bandage on `patient` now: a living teammate, not themselves, hurt and not already
 * being bandaged, on the ground and out of any seat, within reach; the medic alive, grounded, unseated,
 * hands free, with a bandage to spare.
 */
export function canTreat(medic: Person, patient: Person, state: Pick<GameState, 'planets' | 'config'>): boolean {
  if (medic.id === patient.id || medic.teamIndex !== patient.teamIndex) return false;
  if (!medic.alive || medic.airborne || medic.mount || !handsFree(medic) || bandagesLeft(medic) <= 0) return false;
  if (!patient.alive || patient.airborne || patient.mount || patient.health <= 0 || patient.health >= BANDAGE.maxHealth || patient.healLeft !== undefined) return false;
  return apart(medic, patient, state.planets) <= medicReach(state);
}

/** The nearest teammate `medic` could bandage now whose health is at or under `below` (null when none). */
export function nearestPatient(medic: Person, state: Pick<GameState, 'players' | 'planets' | 'config'>, below = BANDAGE.maxHealth - 1): PlayerState | null {
  let best: PlayerState | null = null, bestD = Infinity;
  // (62.60 lever 1: the hurt, filtered once per players array and threshold, in its order — every medic of a tick reads them)
  let byBelow = hurtMemo.get(state.players);
  if (!byBelow) hurtMemo.set(state.players, byBelow = new Map());
  let hurt = byBelow.get(below);
  if (!hurt) byBelow.set(below, hurt = state.players.filter(p => !(p.health > below)));
  for (const p of hurt) {
    if (p.health > below || !canTreat(medic, p, state)) continue;
    const d = apart(medic, p, state.planets);
    if (d < bestD) { best = p; bestD = d; }
  }
  return best;
}

const hurtMemo = new WeakMap<readonly PlayerState[], Map<number, PlayerState[]>>();

export type TreatEvent = { kind: 'bandaged'; unitId: number; healed: number; by: number } | { kind: 'bandageStopped'; unitId: number };

/**
 * One tick of the medics: every treatment under way runs (the patient's timer; when it runs out the patient is healed
 * and the medic's bandage spent) or stops (the patient gone, moved out of reach or cancelled; the medic firing or
 * jumping), then new ones start from this tick's intents (`heal` with a `healTarget`). `intents` is indexed like
 * `players`. Returns null when nothing changed.
 */
export function stepTreatments(state: Pick<GameState, 'players' | 'planets' | 'config'>, intents: readonly UnitIntent[], dt: number): { players: PlayerState[]; events: TreatEvent[] } | null {
  let players: PlayerState[] | null = null;
  const events: TreatEvent[] = [];
  const get = () => players ?? (players = state.players.slice());
  const put = (p: PlayerState) => { const arr = get(); arr[arr.findIndex(q => q.id === p.id)] = p; };
  const at = (id: number) => (players ?? state.players).find(q => q.id === id);
  // treatments under way
  for (let i = 0; i < state.players.length; i++) {
    const medic = state.players[i];
    if (medic.treating === undefined) continue;
    const patient = at(medic.treating), intent = intents[i];
    const stop = !patient || !medic.alive || patient.healLeft === undefined || patient.healBy !== medic.id || !patient.alive || patient.airborne || patient.mount
      || intent.jump || intent.forward !== 0 || intent.right !== 0 || apart(medic, patient, state.planets) > medicReach(state) * 1.25;   // 65.11: a step or a jump breaks it off (the weapon is down meanwhile)   // a little slack once started
    if (stop) {
      put({ ...medic, treating: undefined });
      if (patient && patient.healLeft !== undefined && patient.healBy === medic.id) { put(cancelBandage(patient)); events.push({ kind: 'bandageStopped', unitId: patient.id }); }
      continue;
    }
    const left = patient.healLeft ?? 0;
    if (left - dt > 0) { put({ ...patient, healLeft: left - dt }); continue; }
    // done: the patient healed, the medic's bandage spent
    const healed = Math.min(BANDAGE.maxHealth, patient.health + BANDAGE.heals) - patient.health;
    put({ ...patient, health: patient.health + healed, healLeft: undefined, healTime: undefined, healBy: undefined });
    put({ ...medic, treating: undefined, bandages: bandagesLeft(medic) - 1 });
    events.push({ kind: 'bandaged', unitId: patient.id, healed, by: medic.id });
  }
  // new ones
  for (let i = 0; i < state.players.length; i++) {
    const intent = intents[i];
    if (!intent.heal || intent.healTarget === undefined) continue;
    const medic = at(state.players[i].id)!, patient = at(intent.healTarget);
    if (!patient || !canTreat(medic, patient, state)) continue;
    // 65.11: only on someone standing still — a patient walking off would break it off the tick it began (lane B's
    // every-switch check: 87 of 88 stopped bandages were a medic starting on a teammate on the move)
    const pi = state.players.findIndex(q => q.id === patient.id), pIntent = intents[pi];
    if (pIntent && (pIntent.jump || pIntent.forward !== 0 || pIntent.right !== 0)) continue;
    put({ ...medic, treating: patient.id });
    put({ ...patient, healLeft: BANDAGE.seconds, healTime: BANDAGE.seconds, healBy: medic.id });
  }
  return players ? { players, events } : null;
}
